import { Typography } from "@material-ui/core";
import React, { FC, useContext } from "react";
import { useIntl } from "react-intl";
import { ActionMeta, ValueType } from "react-select/src/types";
import { ExamSessionCandidateExamState } from "../../model/examSessionCandidate/ExamSessionCandidateExamViewModel";
import { OralExamOptionTranslationKey } from "../../model/OralExamOption";
import { RoomViewModel } from "../../model/RoomViewModel";
import { GetTranslation } from "../../translations/GetTranslation";
import CustomSelect, { OptionType } from "../common/customSelect/CustomSelect";
import FormGridCell from "../common/FormGridCell";
import TranslatedMessage from "../translations/TranslatedMessage";
import { SchedulerCandidateContextValue, SchedulerExamCandidate } from "./context/SchedulerCandidateContext";
import { SchedulerContextValue } from "./context/SchedulerContext";
import { isOralDay } from "./context/SchedulerContextUtils";

interface SchedulerCandidateSelectProps {
  selectedCandidates: SchedulerExamCandidate[];
  examId?: string;
  room?: RoomViewModel;
  timeSlotIndex?: number;
}

const SchedulerCandidateSelect: FC<SchedulerCandidateSelectProps> = ({
  selectedCandidates,
  examId,
  room,
  timeSlotIndex,
}) => {
  const intl = useIntl();
  const { examTypes, readonly, selectedExamDayId } = useContext(SchedulerContextValue);
  const {
    schedulerCandidates,
    schedulerExamCandidates,
    addCandidate: handleAddCandidate,
    removeCandidate: handleRemoveCandidate,
    clearCandidates: handleClearCandidates,
  } = useContext(SchedulerCandidateContextValue);

  const getCandidateLabel = (schedulerExamCandidate: SchedulerExamCandidate) => {
    const { candidateId, moduleOption } = schedulerExamCandidate;
    const candidate = schedulerCandidates.find((candidate) => candidate.candidateId === candidateId);
    if (candidate === undefined) {
      return candidateId;
    }

    const name = `${candidate.firstName} ${candidate.lastName}`.trim();
    const optionLabel = GetTranslation(intl, OralExamOptionTranslationKey[moduleOption]);
    return `${name} ${candidate.language} - ${optionLabel}`;
  };

  if (readonly) {
    const getReadonlyCandidateElement = (schedulerExamCandidate: SchedulerExamCandidate) => {
      const { candidateId } = schedulerExamCandidate;
      const label = getCandidateLabel(schedulerExamCandidate);
      return (
        <FormGridCell size="wide" key={candidateId}>
          <Typography variant="body1">{label}</Typography>
        </FormGridCell>
      );
    };

    return <>{selectedCandidates.map(getReadonlyCandidateElement)}</>;
  }

  if (examId === undefined || room === undefined || timeSlotIndex === undefined) {
    return null;
  }

  const isMulti = !isOralDay(examTypes);

  const mapToOption = (schedulerExamCandidate: SchedulerExamCandidate): OptionType => {
    const { candidateId } = schedulerExamCandidate;
    const label = getCandidateLabel(schedulerExamCandidate);
    return { label, value: candidateId } as OptionType;
  };

  const handleChange = (valueType: ValueType<OptionType>, action: ActionMeta<OptionType> | undefined) => {
    if (!action) {
      return;
    }

    if (action.action === "clear") {
      handleClearCandidates(selectedExamDayId, room.id, timeSlotIndex);
      return;
    }

    if (action.removedValue && action.action === "remove-value") {
      handleRemoveCandidate(action.removedValue.value, examId);
      return;
    }

    if (action.action === "select-option") {
      if (isMulti) {
        if (action.option !== undefined) {
          handleAddCandidate(action.option?.value, examId, selectedExamDayId, room.id, timeSlotIndex);
        }
      } else {
        if (valueType !== undefined && valueType !== null) {
          const optionType = valueType as OptionType;
          handleAddCandidate(optionType.value, examId, selectedExamDayId, room.id, timeSlotIndex);
        }
      }
    }
  };

  const selectedCandidateOptions = selectedCandidates.map(mapToOption);

  const availableCandidateOptions = schedulerExamCandidates
    .filter(
      (schedulerExamCandidate) =>
        schedulerExamCandidate.examId === examId &&
        schedulerExamCandidate.examState === ExamSessionCandidateExamState.Cancelled
    )
    .map(mapToOption);

  const isFull = selectedCandidateOptions.length === room.numberOfSeats;
  const isOverloaded = selectedCandidateOptions.length > room.numberOfSeats;

  const getLabel = () => {
    if (isOverloaded) {
      return <TranslatedMessage id="userRoomAssignment.label.roomOverload" />;
    }

    if (isFull) {
      return <TranslatedMessage id="userRoomAssignment.label.roomFull" />;
    }

    return "";
  };

  return (
    <CustomSelect
      error={isOverloaded}
      xs={12}
      required={false}
      value={selectedCandidateOptions}
      name=""
      label={getLabel()}
      placeholder={""}
      options={isFull || isOverloaded ? [] : availableCandidateOptions}
      isMulti={isMulti}
      isClearable={true}
      onChange={handleChange}
    />
  );
};

export default SchedulerCandidateSelect;
